import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import PageHeader from '../../components/ui/PageHeader';
import DialogHeader from '../../components/ui/DialogHeader';
import TicketVenta from '../ventas/components/TicketVenta';
import { VentaService } from '../../services/VentaService';
import { GastoService } from '../../services/GastoService';
import { DetalleVentaService } from '../../services/DetalleVentaService';
import { EmpresaService } from '../../services/EmpresaService';
import { ClienteService } from '../../services/ClienteService';

const Reportes = () => {
    const toast = useRef(null);
    const [loading, setLoading] = useState(false);
    const [ventas, setVentas] = useState([]);
    const [gastos, setGastos] = useState([]);

    // Re-impresión
    const [ticketDialogVisible, setTicketDialogVisible] = useState(false);
    const [ticketData, setTicketData] = useState(null);
    const [loadingTicket, setLoadingTicket] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [ventasResponse, gastosResponse] = await Promise.all([
                VentaService.getAll(),
                GastoService.getAll()
            ]);
            const allVentas = ventasResponse?.data || [];
            const ventasCompletadas = allVentas.filter((v) => v.estado === 'completada' || v.estado === 'COMPLETADA');
            setVentas(ventasCompletadas);
            setGastos(gastosResponse?.data || []);
        } catch (error) {
            toast.current.show({ severity: 'warn', summary: 'Atencion', detail: 'No se pudo cargar toda la informacion de reportes', life: 3500 });
        } finally {
            setLoading(false);
        }
    };

    const totalVentas = useMemo(() => ventas.reduce((acc, v) => acc + Number(v.montoTotal || 0), 0), [ventas]);
    const totalGastos = useMemo(() => gastos.reduce((acc, g) => acc + Number(g.monto || 0), 0), [gastos]);
    const utilidadEstimada = useMemo(() => totalVentas - totalGastos, [totalVentas, totalGastos]);
    const ticketPromedio = useMemo(() => (ventas.length ? totalVentas / ventas.length : 0), [totalVentas, ventas.length]);

    const dineroBody = (row, field) => `S/ ${Number(row[field] || 0).toFixed(2)}`;
    const estadoBody = (row) => {
        const estado = (row.estado || '').toUpperCase();
        const severity = estado === 'COMPLETADA' ? 'success' : 'warning';
        return <Tag value={estado || 'N/A'} severity={severity} />;
    };

    // ====== Re-impresión de ticket ======
    const reimprimir = async (venta) => {
        setLoadingTicket(true);
        try {
            // Obtener detalles de la venta, empresa y cliente en paralelo
            const [detallesRes, empresaRes, clienteRes] = await Promise.all([
                DetalleVentaService.getByVenta(venta.id).catch(() => ({ data: [] })),
                EmpresaService.getEmpresas().catch(() => []),
                venta.idCliente ? ClienteService.getById(venta.idCliente).catch(() => null) : Promise.resolve(null)
            ]);

            const detalles = detallesRes?.data || detallesRes || [];
            const empresa = (empresaRes && empresaRes.length > 0) ? empresaRes[0] : null;
            const cliente = clienteRes?.data || clienteRes || null;

            // Mapear detalles a formato compatible con TicketVenta
            const detallesMapeados = (Array.isArray(detalles) ? detalles : []).map(d => ({
                cantidad: d.cantidad,
                precio: d.precioVenta || d.precioUnitario || 0,
                precioUnitario: d.precioVenta || d.precioUnitario || 0,
                nombre: d.descripcion || `Variante #${d.idVariante}`,
                variante: { displayName: d.descripcion || `Variante #${d.idVariante}` }
            }));

            setTicketData({
                venta: {
                    ...venta,
                    fechaCreacion: venta.fecha || venta.fechaCreacion
                },
                detalles: detallesMapeados,
                empresa,
                cliente
            });
            setTicketDialogVisible(true);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar los datos del ticket para re-impresión', life: 3500 });
        } finally {
            setLoadingTicket(false);
        }
    };

    const accionesBody = (rowData) => (
        <Button
            icon={loadingTicket ? "pi pi-spin pi-spinner" : "pi pi-print"}
            className="p-button-rounded p-button-text p-button-sm"
            onClick={() => reimprimir(rowData)}
            tooltip="Re-imprimir Ticket"
            tooltipOptions={{ position: 'top' }}
            disabled={loadingTicket}
        />
    );

    const ticketDialogFooter = (
        <div className="flex justify-content-between">
            <Button label="Cerrar" icon="pi pi-times" onClick={() => setTicketDialogVisible(false)} className="p-button-text p-button-secondary" />
            <Button label="Imprimir" icon="pi pi-print" onClick={() => window.print()} className="p-button-primary" autoFocus />
        </div>
    );

    return (
        <div className="grid">
            <Toast ref={toast} />

            <div className="col-12">
                <PageHeader 
                    title="Reportes y Cierres" 
                    description="Resumen financiero de ventas, gastos y utilidad del negocio." 
                    icon="pi pi-chart-bar" 
                />
            </div>

            <div className="col-12 md:col-6 xl:col-3">
                <Card title="Ventas" subTitle="Total completadas" className="h-full">
                    <h2 className="m-0">S/ {totalVentas.toFixed(2)}</h2>
                </Card>
            </div>
            <div className="col-12 md:col-6 xl:col-3">
                <Card title="Gastos" subTitle="Total registrado" className="h-full">
                    <h2 className="m-0">S/ {totalGastos.toFixed(2)}</h2>
                </Card>
            </div>
            <div className="col-12 md:col-6 xl:col-3">
                <Card title="Utilidad" subTitle="Estimacion" className="h-full">
                    <h2 className="m-0">S/ {utilidadEstimada.toFixed(2)}</h2>
                </Card>
            </div>
            <div className="col-12 md:col-6 xl:col-3">
                <Card title="Ticket Promedio" subTitle="Por venta" className="h-full">
                    <h2 className="m-0">S/ {ticketPromedio.toFixed(2)}</h2>
                </Card>
            </div>

            <div className="col-12">
                <div className="card">
                    <h4>Ventas recientes</h4>
                    <DataTable value={ventas.slice(0, 30)} loading={loading} paginator rows={10} rowsPerPageOptions={[10, 20, 30]} responsiveLayout="scroll"
                        emptyMessage="No hay ventas completadas para mostrar.">
                        <Column field="codigoVenta" header="Codigo" sortable style={{ minWidth: '10rem' }} />
                        <Column field="fecha" header="Fecha" body={(row) => (row.fecha ? new Date(row.fecha).toLocaleString() : '-')} sortable style={{ minWidth: '12rem' }} />
                        <Column field="estado" header="Estado" body={estadoBody} sortable style={{ minWidth: '8rem' }} />
                        <Column field="tipoDePago" header="Pago" sortable style={{ minWidth: '8rem' }} />
                        <Column field="montoTotal" header="Total" body={(row) => dineroBody(row, 'montoTotal')} sortable style={{ minWidth: '8rem' }} />
                        <Column header="Ticket" body={accionesBody} style={{ minWidth: '5rem', textAlign: 'center' }} />
                    </DataTable>
                </div>
            </div>

            {/* Dialog de Re-impresión */}
            <Dialog
                visible={ticketDialogVisible}
                className="w-full md:w-5 lg:w-4"
                header={<DialogHeader title="Re-impresión de Ticket" icon="pi pi-print" />}
                modal
                footer={ticketDialogFooter}
                onHide={() => setTicketDialogVisible(false)}
            >
                {ticketData && (
                    <TicketVenta
                        venta={ticketData.venta}
                        detalles={ticketData.detalles}
                        empresa={ticketData.empresa}
                        cliente={ticketData.cliente}
                        esReimpresion={true}
                    />
                )}
            </Dialog>
        </div>
    );
};

export default Reportes;
